import { DisposalShowItem } from '@agency/apis/disposals';
import { InterestScheduleSyncStatus } from '@shared/models/disposal/disposal';
import { IUser } from '@shared/models/IUser';
import { BasicUser } from '@shared/models/user';

import { ObjectType } from './object';

export type PusherDuration = 5000 | 10000 | 20000;

export enum AgencyPageIds {
  DisposalInterestScheduleSpace = 'DisposalInterestScheduleSpace',
}

// --- Keys ---
// NOTE: It's very important to include "." at the beginning of the key

// Disposal (Env)
export enum PusherKeyEnvDisposal {
  DisposalCountsUpdated = '.DisposalCountsUpdated',
  InterestCountsUpdated = '.InterestCountsUpdated',
  InterestMovedToSchedule = '.InterestMovedToSchedule',
  InterestScheduleSync = '.InterestScheduleSync',
}

// Object (Env)
export enum PusherKeyEnvObject {
  ObjectAdded = '.ObjectAdded',
  ObjectDeleted = '.ObjectDeleted',
  ObjectUpdated = '.ObjectUpdated',
  ObjectsUpdated = '.ObjectsUpdated',
}

// Laravel (All)
export enum PusherKeyLaravel {
  BroadcastNotificationCreated = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
}

// Society (All)
export enum PusherKeySociety {
  // TODO: These should be scoped to the env. Currently emit on all envs
  AcquisitionPublishedToSociety = '.AcquisitionPublishedToSociety',
  LettingPublishedToSociety = '.LettingPublishedToSociety',
}

// User (All)
export enum PusherKeyUser {
  // TODO: These should be scoped to the env. Currently emit on all envs
  AtlasTransactionFailure = '.AtlasTransactionFailure',
  BulkActionCompleted = '.BulkActionCompleted',
  BulkActionProgress = '.BulkActionProgress',
  BulkActionFailure = '.BulkActionFailure',
}

// User (Env)
export enum PusherKeyEnvUser {
  ScheduleMatchAgentMessageCreated = '.ScheduleMatchAgentMessageCreated',
}

// Atlas Export Events
export enum PusherKeyAtlasReport {
  ReportCompleted = '.report-completed',
  ReportError = '.report-error',
}

export type PusherKey =
  | PusherKeyAtlasReport
  | PusherKeyEnvDisposal
  | PusherKeyEnvObject
  | PusherKeyEnvUser
  | PusherKeyLaravel
  | PusherKeySociety
  | PusherKeyUser;

// --- Payloads ---

interface LegacyEventPayload {
  id: number;
  user: IUser; // TODO: Check ok
  headline: string;
  subline: string;
  message: string;
}

export interface DisposalObjectPayload {
  object_type: ObjectType.Disposal;
  // Data
  changed_keys: string[] | null;
  disposal_id: number;
}

export interface RequirementCommentObjectPayload {
  object_type: ObjectType.RequirementComment;
  // Data
  comment_id: number;
  requirement_id: number;
}

export interface RequirementObjectPayload {
  object_type: ObjectType.Requirement;
  // Data
  changed_keys: string[] | null;
  requirement_id: number;
}

export interface ScheduleMatchObjectPayload {
  object_type: ObjectType.ScheduleMatch;
  // Data
  changed_keys: string[] | null;
  disposal_id: number;
  schedule_match_id: number;
}

export interface ScheduleMatchCommentObjectPayload {
  object_type: ObjectType.ScheduleMatchComment;
  // Data
  comment_id: number;
  disposal_id: number;
  schedule_match_id: number;
}

export interface ScheduleMatchMessageObjectPayload {
  object_type: ObjectType.ScheduleMatchMessage;
  // Data
  disposal_id: number;
  message_id: number;
  schedule_match_id: number;
}

export interface ScheduleMatchViewingObjectPayload {
  object_type: ObjectType.ScheduleMatchViewing;
  // Data
  disposal_id: number;
  schedule_match_id: number;
  viewing_id: number;
}

export type ObjectPayloads =
  | DisposalObjectPayload
  | RequirementCommentObjectPayload
  | RequirementObjectPayload
  | ScheduleMatchObjectPayload
  | ScheduleMatchCommentObjectPayload
  | ScheduleMatchMessageObjectPayload
  | ScheduleMatchViewingObjectPayload;

export interface ScheduleMatchBulkObjectPayload {
  object_type: ObjectType.ScheduleMatch;
  // Data
  disposal_id: number;
  object_ids: number[];
}

export type BulkObjectPayloads = ScheduleMatchBulkObjectPayload;

export type PusherPayload = {
  // ---- Disposal (Env) ----
  [PusherKeyEnvDisposal.DisposalCountsUpdated]: {
    disposal_id: number;
    counts: DisposalShowItem['counts'];
  };

  [PusherKeyEnvDisposal.InterestCountsUpdated]: {
    counts: {
      messages: number;
    };
    interest_id: number;
    is_last_sender: boolean;
  };

  [PusherKeyEnvDisposal.InterestMovedToSchedule]: {
    id: number;
  };

  [PusherKeyEnvDisposal.InterestScheduleSync]: {
    sync_status: InterestScheduleSyncStatus;
  };

  // ---- Object (Env) ----

  [PusherKeyEnvObject.ObjectAdded]: ObjectPayloads;

  [PusherKeyEnvObject.ObjectDeleted]: ObjectPayloads;

  [PusherKeyEnvObject.ObjectUpdated]: ObjectPayloads;

  [PusherKeyEnvObject.ObjectsUpdated]: BulkObjectPayloads;

  // ---- Laravel (All) ----

  [PusherKeyLaravel.BroadcastNotificationCreated]: ValueOf<PusherNotificationPayload>;

  // ---- Society (All) ----

  [PusherKeySociety.AcquisitionPublishedToSociety]: LegacyEventPayload;

  [PusherKeySociety.LettingPublishedToSociety]: LegacyEventPayload;

  // ---- User (All) ----

  [PusherKeyUser.AtlasTransactionFailure]: {
    title: string;
    transactionId: number;
    url: string;
  };

  [PusherKeyUser.BulkActionCompleted]: {
    failed_text: string | null;
    job_id: number;
    subtext: string;
    success_text: string;
    title: string;
    type: PusherKeyUser.BulkActionCompleted;
    url_text: string | null;
    url: string | null;
  };

  [PusherKeyUser.BulkActionProgress]: {
    job_id: number;
    progress: number;
    subtext: string;
    title: string;
    type: PusherKeyUser.BulkActionProgress;
  };

  [PusherKeyUser.BulkActionFailure]: {
    failed_text: string;
    job_id: number;
    subtext: string;
    title: string;
    type: PusherKeyUser.BulkActionFailure;
    url_text: string | null;
    url: string | null;
  };

  // ---- User (Env) ----

  // BE: broadcast by the agent-message-created event; consumed by the match drawer's useMatchMessages.
  [PusherKeyEnvUser.ScheduleMatchAgentMessageCreated]: {
    disposal_id: number;
    disposal_organisation_id: number;
    message: {
      body: string;
      created_at: string;
      disposal_organisation_id: number;
      id: number;
      object_id: number;
      object_type: string;
      updated_at: string;
      user_id: number;
      user_organisation_id: number;
      user: BasicUser;
    };
    requirement_id: number;
    requirement_organisation_id: number;
  };

  // ---- Atlas Export Events ----

  [PusherKeyAtlasReport.ReportCompleted]: {
    export_id: string;
    status: 'completed';
    message: string;
    file_url: string;
    file_name: string;
    created_at: string;
    ready_at: string;
  };

  [PusherKeyAtlasReport.ReportError]: {
    export_id: string;
    status: 'error';
    message: string;
  };
};

// --- Notification Keys ---

export enum PusherNotificationKey {
  UserOrTeamMentionedOnInterestScheduleComment = 'userOrTeamMentionedOnInterestScheduleComment',
  UserOrTeamMentionedOnRequirementComment = 'userOrTeamMentionedOnRequirementComment',
  UserOrTeamMentionedOnShortlistComment = 'userOrTeamMentionedOnShortlistComment',
  UserOrTeamMentionedOnSocietyAcquisitionComment = 'userOrTeamMentionedOnSocietyAcquisitionComment',
}

// --- Notification Payloads ---

export type PusherNotificationPayload = {
  [PusherNotificationKey.UserOrTeamMentionedOnInterestScheduleComment]: {
    disposal_id: number;
    headline: string;
    id: number;
    match_id: number;
    message: string;
    subline: string;
    type: PusherNotificationKey.UserOrTeamMentionedOnInterestScheduleComment;
    user: IUser; // TODO: Check ok
  };

  [PusherNotificationKey.UserOrTeamMentionedOnRequirementComment]: {
    acquisition_id: number;
    headline: string;
    id: number;
    message: string;
    subline: string;
    type: PusherNotificationKey.UserOrTeamMentionedOnRequirementComment;
    user: IUser; // TODO: Check ok
  };

  [PusherNotificationKey.UserOrTeamMentionedOnShortlistComment]: {
    headline: string;
    id: number;
    match_id: number;
    message: string;
    requirement_id: number;
    subline: string;
    type: PusherNotificationKey.UserOrTeamMentionedOnShortlistComment;
    user: IUser; // TODO: Check ok
  };

  [PusherNotificationKey.UserOrTeamMentionedOnSocietyAcquisitionComment]: {
    headline: string;
    id: number;
    message: string;
    society_acquisition_id: number;
    subline: string;
    type: PusherNotificationKey.UserOrTeamMentionedOnSocietyAcquisitionComment;
    user: IUser; // TODO: Check ok
  };
};
